import {
    Component,
    ElementRef,
    forwardRef,
    HostBinding,
    input,
    model,
    OnChanges,
    output,
    SimpleChanges,
    viewChild
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    UntypedFormControl,
    Validator
} from '@angular/forms';
import {
    createSeparatorMask,
    handleCaret,
    isSafari,
    unMaskSeparatorValue
} from '@trade-platform/ui-utils';
import { AixDataTestingDirective } from '../../directives/data-testing/data-testing.directive';

@Component({
    selector: 'aix-phone',
    styleUrls: ['./aix-phone.component.scss'],
    templateUrl: './aix-phone.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => AixPhoneComponent)
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: AixPhoneComponent
        }
    ],
    standalone: true,
    imports: [AixDataTestingDirective]
})
export class AixPhoneComponent implements ControlValueAccessor, Validator, OnChanges {
    _propagateChanges: (value: string) => void = () => ({});
    _propagateTouches: () => void = () => ({});

    config = input<any>();
    isStandalone = input<boolean>(true);
    isRequired = input<boolean>(false);
    isDisabled = input<boolean>(false);
    isValid = input<boolean>(false);
    isDirty = model<boolean>(false);
    initialValue = input<string>();
    hint = input<string>();

    valueChanges = output<string>();
    setDirty = output();

    inputField = viewChild<ElementRef<HTMLInputElement>>('inputField');

    @HostBinding('attr.aix-control')
    aixControl: string;

    private readonly MAX_LENGTH = 10;
    private caretPositionStart: number;
    private caretPositionEnd: number;
    private keyDownEvent: KeyboardEvent;
    private mask = createSeparatorMask([3, 6], this.MAX_LENGTH, '-');

    constructor() {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.initialValue) {
            this.valueToSend(this.initialValue() || '');
        }
    }

    onKeyDown(e: KeyboardEvent & { target: any }) {
        this.caretPositionStart = e.target.selectionStart;
        this.caretPositionEnd = e.target.selectionEnd;
        this.keyDownEvent = e;
    }

    valueToSend(rawValue: string) {
        const unmaskedValue = unMaskSeparatorValue(rawValue, this.MAX_LENGTH);
        (<ElementRef>this.inputField()).nativeElement.value = this.mask(unmaskedValue as string);

        if (isSafari) {
            handleCaret(
                [3, 7],
                this.keyDownEvent,
                (<ElementRef>this.inputField()).nativeElement as HTMLInputElement,
                this.caretPositionStart,
                this.caretPositionEnd
            );
        }

        return unmaskedValue && unmaskedValue.length ? unmaskedValue : '';
    }

    onUserInput(evt: Event | ClipboardEvent) {
        const val = this.valueToSend((evt.target as HTMLInputElement).value);
        if (!this.isDirty()) {
            this.setDirty.emit();

            // It's standalone, we set the dirty state here instead through the store
            if (this.isStandalone()) {
                this.isDirty.set(true);
            }
        }
        this._propagateChanges(val);
        this.valueChanges.emit(val);
    }

    validate({ value }: UntypedFormControl) {
        return !value || value.match(/\d\d\d\d\d\d\d\d\d\d/g) ? null : { invalid: true };
    }

    writeValue(value: any) {
        this.valueToSend(value);
    }

    registerOnChange(fn: (value: string) => void) {
        this._propagateChanges = fn;
    }

    registerOnTouched(fn: () => void) {
        this._propagateTouches = fn;
    }
}
